import { Component, OnInit, inject, ViewChildren, QueryList, PLATFORM_ID, Inject } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { InputComponent } from '../../input/input.component';
import { ButtonComponent } from '../../button/button.component';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { LanguageRoutingService } from '../../../services/language-routing.service';
import { AuthService } from '../../../services/auth.service';
import { finalize } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { CardComponent } from '../../card/card.component';
import { AlertCardComponent } from '../../alert-card/alert-card.component';
import { GoogleAuthProvider, getAuth } from 'firebase/auth';
import { signInWithPopup } from '@angular/fire/auth';
import { GoogleRegister } from '../../../../../../../libs/shared/src/lib/models/dashboard/user-from-be';
import { GoogleAuthService } from '../../../services/google-auth.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, InputComponent, ButtonComponent, TranslocoModule, RouterModule, CardComponent, AlertCardComponent],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  providers: [LanguageRoutingService],
})
export class LoginComponent implements OnInit {
  loginForm: FormGroup;
  isSubmitting = false;
  loginError = '';
  afterEmailVerification = false;
  token: string | null = null;
  redirect: string | null = null;
  formSubmitted = false; // Track if form was submitted to show validation errors

  // Access all input components to manually mark them as touched
  @ViewChildren(InputComponent) inputComponents!: QueryList<InputComponent>;

  private fb = inject(FormBuilder);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private authService = inject(AuthService);
  private transloco = inject(TranslocoService);
  private fireAuth = inject(AngularFireAuth);
  public languageRoutingService = inject(LanguageRoutingService);
  private googleAuthService = inject(GoogleAuthService);
  private platformId = inject(PLATFORM_ID);

  constructor() {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
    });
  }

  ngOnInit(): void {
    // Check for redirect parameter
    this.redirect = this.activatedRoute.snapshot.queryParams['redirect'] as string;

    // Only run email verification in the browser, not on the server (SSR)
    if (isPlatformBrowser(this.platformId)) {
      // Check for verification token
      this.token = this.activatedRoute.snapshot.queryParamMap.get('token');
      if (this.token !== null) {
        this.verifyEmail(this.token);
      }

      // Check login status
      this.checkLoginStatus();
    }
  }

  getErrorMessage(controlName: string): string {
    const control = this.loginForm.get(controlName);
    if (!control) return '';

    // Only show errors if field is touched OR form was submitted
    if (!control.touched && !this.formSubmitted) return '';

    if (control.hasError('required')) {
      return this.transloco.translate('auth.validation.required');
    }

    if (control.hasError('email')) {
      return this.transloco.translate('auth.validation.email');
    }

    if (control.hasError('minlength')) {
      return this.transloco.translate('auth.validation.minLength', { count: control.getError('minlength').requiredLength });
    }

    return '';
  }

  onSubmit(): void {
    this.formSubmitted = true; // Mark form as submitted to show all errors

    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();

      // Also mark input components' internal touched state
      this.inputComponents?.forEach((input: InputComponent) => input.markAsTouched());

      return;
    }

    this.isSubmitting = true;
    this.loginError = '';

    // Use Firebase authentication
    this.login();
  }

  async login(): Promise<void> {
    let idToken: string = '';
    try {
      const dataF = await this.fireAuth.signInWithEmailAndPassword(this.loginForm.get('email')?.value, this.loginForm.get('password')?.value);
      idToken = await dataF.user!.getIdToken();
    } catch (error: any) {
      this.isSubmitting = false;
      if (error.code === 'auth/invalid-credential') {
        this.transloco.selectTranslate('auth.login.unauthorized').subscribe((message: string) => {
          this.loginError = message;
        });
      } else {
        this.transloco.selectTranslate('auth.login.error').subscribe((message: string) => {
          this.loginError = message;
        });
      }
      return;
    }

    this.authService
      .login(idToken)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (res) => {
          if (res) {
            if (this.redirect) {
              this.router.navigate([this.redirect]);
            } else {
              // Prefer the URL's current language so users who land on /en/login stay in EN.
              // Only fall back to the user's saved preference when the URL has no language prefix.
              const urlLang = this.languageRoutingService.extractLanguageFromUrl();
              const userLang = urlLang || res.language || 'en';
              this.router.navigate([`/${userLang}`]);
            }
          }
        },
        error: (error: HttpErrorResponse) => {
          let errorMessage = 'auth.login.unauthorized';

          if (error.error?.message === 'EMAIL_NOT_VERIFIED') {
            errorMessage = 'auth.login.emailNotVerified';
          }

          this.transloco.selectTranslate(errorMessage).subscribe((message: string) => {
            this.loginError = message;
          });
        },
      });
  }

  checkLoginStatus(): void {
    if (this.authService.isLoggedIn()) {
      this.router.navigate(['']);
    } else if (this.afterEmailVerification) {
      this.router.navigate(['login']);
    }
  }

  verifyEmail(token: string): void {
    this.authService.verifyEmail(token).subscribe({
      next: (res) => {
        this.transloco.selectTranslate('auth.login.emailVerified').subscribe((message: string) => {
          this.loginError = message;
        });
        this.afterEmailVerification = true;
        this.checkLoginStatus();
      },
      error: (error) => {
        let messageKey = 'auth.login.linkInvalid';

        if (error === 'Email Already Verified') {
          messageKey = 'auth.login.verificationLinkUsedAlready';
        }

        this.transloco.selectTranslate(messageKey).subscribe((message: string) => {
          this.loginError = message;
        });

        this.afterEmailVerification = true;
        this.checkLoginStatus();
      },
    });
  }

  googleAuth = async (): Promise<any> => {
    const provider = new GoogleAuthProvider();
    const auth = getAuth();
    signInWithPopup(auth, provider)
      .then((result) => {
        const user = result.user;
        let newUser = new GoogleRegister();
        newUser.isGoogle = true;
        newUser.name = user.displayName || '';
        newUser.email = user.email || '';
        newUser.emailVerified = user.emailVerified;
        newUser.uid = user.uid;
        if (user.emailVerified == true) {
          user.getIdToken().then((idToken) => {
            this.googleAuthService.sendGoogleUserData(newUser);

            this.authService.googleRegister(newUser).subscribe({
              next: (res) => {
                if (res === 'NOT_REGISTERED') {
                  const currentLang = this.languageRoutingService.getCurrentLanguage();
                  this.router.navigate([`/${currentLang}/register`]);
                } else if (res === 'REGISTERED') {
                  this.authService.login(idToken).subscribe({
                    next: (res) => {
                      if (res) {
                        if (this.redirect) {
                          this.router.navigate([this.redirect]);
                        } else {
                          // Navigate to home page with user's language from backend
                          const userLang = res.language || 'en';
                          this.router.navigate([`/${userLang}`]);
                        }
                      }
                    },
                    error: (error: HttpErrorResponse) => {
                      this.transloco.selectTranslate('auth.login.error').subscribe((message: string) => {
                        this.loginError = message;
                      });
                    },
                  });
                }
              },
              error: (error) => {
                this.transloco.selectTranslate('auth.login.error').subscribe((message: string) => {
                  this.loginError = message;
                });
              },
            });
          });
        }
      })
      .catch((error) => {
        this.transloco.selectTranslate('auth.login.error').subscribe((message: string) => {
          this.loginError = message;
        });
      });
  };
}
